import type { ReactNode } from "react";

/** Неразрывный пробел: сумма не должна переноситься между разрядом и рублём. */
const NBSP = " ";

/**
 * Копейки в рубли.
 *
 * Копейки не показываются, когда их нет: «250 ₽» вместо «250,00 ₽». В меню
 * круглые цены — правило, а не исключение, и лишние нули превращают ценник
 * в бухгалтерию.
 */
export function money(kopecks: number | null | undefined): string {
  const value = Math.round(kopecks ?? 0);
  const whole = Math.trunc(value / 100);
  const fraction = Math.abs(value % 100);

  const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  const tail = fraction === 0 ? "" : `,${String(fraction).padStart(2, "0")}`;

  return `${grouped}${tail}${NBSP}₽`;
}

/**
 * Подложка под заглушку фотографии — своя у каждого названия, но постоянная.
 *
 * Товар без фотографии — не исключение, а норма первых недель: партнёр
 * заводит меню быстрее, чем снимает еду. Серый прямоугольник на этом месте
 * выглядит поломкой, а спокойное пятно — оформлением.
 */
// Оттенки серого: цветных пятен в палитре нет, но одинаковые заглушки
// сливаются в списке, и монограммы должны хоть чем-то отличаться.
const TINTS = ["#e9e9ec", "#dedee2", "#f0f0f2", "#d4d4d8", "#e4e4e7", "#ececef"];

export function tintFor(seed: string): string {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) % 997;
  }
  return TINTS[hash % TINTS.length]!;
}

/**
 * Монограмма из названия: «Пироги у пруда» → «ПУ».
 *
 * Слова очищаются от кавычек и скобок: у «Мороженое «Пломбир»» второе слово
 * начинается с кавычки, и без очистки монограмма выходит «М«».
 */
export function monogram(title: string): string {
  return title
    .split(/\s+/)
    .map(word => word.replace(/[^\p{L}\p{N}]/gu, ""))
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0]!.toUpperCase())
    .join("");
}

export function Photo({
  src,
  title,
  className
}: {
  src: string | null;
  title: string;
  className?: string;
}) {
  return (
    <div className={className} style={src ? undefined : { background: tintFor(title) }}>
      {src ? <img src={src} alt={title} loading="lazy" /> : monogram(title)}
    </div>
  );
}

export function Spinner({ text }: { text: string }) {
  return (
    <div className="center">
      <div className="spinner" />
      <p className="muted">{text}</p>
    </div>
  );
}

export function Sheet({
  title,
  note,
  onClose,
  children,
  footer
}: {
  title: string;
  note?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <>
      <div className="scrim" onClick={onClose} aria-hidden="true" />
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title}>
        <div className="sheet-inner">
          <div className="sheet-grip" />
          <div className="sheet-head">
            <h2>{title}</h2>
            {note ? <p className="muted small">{note}</p> : null}
          </div>
          <div className="sheet-body">{children}</div>
          {footer ? <div className="sheet-foot">{footer}</div> : null}
        </div>
      </div>
    </>
  );
}

/** Вес и объём в одну подпись: заполнено обычно что-то одно. */
export function measure(weightG: number | null, volumeMl: number | null): string {
  const parts: string[] = [];
  if (weightG) parts.push(`${weightG}${NBSP}г`);
  if (volumeMl) parts.push(`${volumeMl}${NBSP}мл`);
  return parts.join(" · ");
}
